import { Tags } from "lucide-react";

import { VIEWPORT_PANEL_EYEBROW_CLASS, ViewportToolsetSubButton } from "@/viewport-tools/core";
import { cn } from "@/lib/utils";
import type {
  TagsToolsetRailController,
  ViewportToolsetRailRendererResult,
} from "@/viewport-tools/rail/types";
import { TAG_TOOL_IDS, TAG_TOOLSET, getTagToolDefinition } from "@/viewport-tools/tags/registry";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const NO_TAG_OPTION_VALUE = "__no_tag_option__";

function TagsToolsetPanel({ controller }: { controller: TagsToolsetRailController }) {
  const addTool = getTagToolDefinition(TAG_TOOL_IDS.add);
  const DuplicateIcon = getTagToolDefinition(TAG_TOOL_IDS.duplicate).icon;
  const AddIcon = addTool.icon;

  return (
    <div className="relative flex flex-col">
      <ViewportToolsetSubButton
        icon={<AddIcon className="size-4" />}
        label={addTool.label}
        active={controller.activeTool === TAG_TOOL_IDS.add}
        disabled={controller.tagOptions.length === 0}
        onClick={() =>
          controller.selectTool(
            controller.activeTool === TAG_TOOL_IDS.add ? null : TAG_TOOL_IDS.add
          )
        }
      />
      {controller.activeTool === TAG_TOOL_IDS.add ? (
        <div className="pointer-events-auto absolute left-full top-0 ms-1 z-10 w-48 rounded-md border border-border/70 bg-background/95 p-2 shadow-lg backdrop-blur">
          <div className={cn("mb-1", VIEWPORT_PANEL_EYEBROW_CLASS)}>
            Tag to place
          </div>
          <Select
            value={controller.selectedTagOptionId ?? NO_TAG_OPTION_VALUE}
            onValueChange={(value) => {
              if (value !== NO_TAG_OPTION_VALUE) {
                controller.setSelectedTagOptionId(value);
              }
            }}
            disabled={controller.tagOptions.length === 0}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue placeholder="Select tag" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_TAG_OPTION_VALUE} disabled>
                Select tag
              </SelectItem>
              {controller.tagOptions.map((tag) => (
                <SelectItem key={tag.id} value={tag.id}>
                  {tag.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : null}
      <ViewportToolsetSubButton
        icon={<DuplicateIcon className="size-4" />}
        label="Duplicate"
        active={controller.activeTool === TAG_TOOL_IDS.duplicate}
        onClick={() =>
          controller.selectTool(
            controller.activeTool === TAG_TOOL_IDS.duplicate ? null : TAG_TOOL_IDS.duplicate
          )
        }
      />
    </div>
  );
}

export function renderTagsToolsetRail(
  controller: TagsToolsetRailController
): ViewportToolsetRailRendererResult {
  return {
    item: {
      id: TAG_TOOLSET.id,
      icon: <Tags className="size-4" />,
      label: TAG_TOOLSET.label,
      sublabel:
        controller.activeTool === TAG_TOOL_IDS.add
          ? "Add"
          : controller.activeTool === TAG_TOOL_IDS.duplicate
            ? "Dup"
            : undefined,
      active: controller.activeTool !== null,
      selected: controller.expanded,
      disabled: controller.disabled,
      onClick: () => controller.setExpanded(!controller.expanded),
      showExpandedIndicator: controller.expanded,
    },
    inlineContent: controller.expanded ? <TagsToolsetPanel controller={controller} /> : null,
  };
}
